import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useError } from "../contexts/ErrorContext";
import { useApiError } from "../hooks/useApiError";
import { colors } from "../colors";
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Avatar,
  InputAdornment,
  IconButton,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  validateComplexPassword,
} from "../utils/validation";
import { useCaptcha } from "../hooks/useCaptcha";

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { showSuccess } = useError();
  const { handleRegistrationError } = useApiError();
  const [formData, setFormData] = useState({
    password: "",
    email: "",
    username: "",
  });
  const [errors, setErrors] = useState({
    username: "",
    email: "",
    password: "",
    captcha: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [captchaValue, setCaptchaValue] = useState("");
  const { captchaUrl, refreshCaptcha, captchaQuery } = useCaptcha(true);

  const validateForm = () => {
    const newErrors: typeof errors = {
      username: "",
      email: "",
      password: "",
      captcha: "",
    };
    let isValid = true;

    if (!formData.username.trim()) {
      newErrors.username = "Full Name is required";
      isValid = false;
    }
    
    // Mobile number validation
    if (!formData.email.trim()) {
      newErrors.email = "Mobile No is required";
      isValid = false;
    } else {
      const mobile = formData.email.trim();
      if (!/^[6-9]/.test(mobile)) {
        newErrors.email = "Mobile number must start with 6, 7, 8, or 9";
        isValid = false;
      } else if (mobile.length !== 10) {
        newErrors.email = "Mobile number must be exactly 10 digits";
        isValid = false;
      }
    }
    
    // Password validation
    if (!formData.password || formData.password.trim() === "") {
      newErrors.password = "Password is required";
      isValid = false;
    } else {
      const passwordValidation = validateComplexPassword(formData.password);
      if (!passwordValidation.isValid) {
        newErrors.password = passwordValidation.errors.join(", ");
        isValid = false;
      }
    }

    // Captcha validation
    if (!captchaValue.trim()) {
      newErrors.captcha = "Captcha is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, username: value }));
    // Clear error when user starts typing
    if (errors.username) {
      setErrors((prev) => ({ ...prev, username: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);

    try {
      // Use the username entered by the user
      const submitData = { ...formData, captcha: captchaValue };
      const registeredUser = await register(submitData);
      showSuccess("Registration successful!");
      
      // Check if user has APP_STUDENT role
      const hasAppStudentRole = registeredUser?.roles?.includes('APP_STUDENT');
      
      if (hasAppStudentRole) {
        // Navigate to school selection page for APP_STUDENT
        navigate("/student-school-selection");
      } else {
        // For other roles, navigate to login
        alert("Registration successful! Please login to continue.");
        navigate("/login");
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "";
      if (typeof message === "string" && message.toLowerCase().includes("captcha")) {
        setErrors((prev) => ({ ...prev, captcha: message || "Invalid captcha" }));
        refreshCaptcha();
      } else {
        setErrors((prev) => ({ ...prev, captcha: "" }));
      }
      handleRegistrationError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    navigate("/");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        background: `linear-gradient(135deg, ${colors.primaryLight} 50%, white 70%)`,
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Main Register Card */}
      <Card
        sx={{
          maxWidth: 450,
          width: "100%",
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          borderRadius: 2,
          mx: 2,
          position: "relative",
        }}
      >
        {/* Close Icon Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            color: "#666",
            transition: 'all 0.2s ease',
            '&:hover': {
              color: "#3092e3",
              transform: 'rotate(90deg) scale(1.1)',
              bgcolor: 'rgba(48, 146, 227, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <CardContent sx={{ p: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#3092e3",
                width: 70,
                height: 70,
                mb: 2,
              }}
            >
              <PersonAddAlt1Icon fontSize="large" />
            </Avatar>
            <Typography
              variant="h4"
              fontWeight="bold"
              gutterBottom
              sx={{
                textAlign: "center",
              }}
            >
              Student Registration
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
            >
              Please fill in the details to create your account.
            </Typography>
          </Box>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.username}
              onChange={handleUsernameChange}
              margin="normal"
              required
              error={!!errors.username}
              helperText={errors.username}
              InputLabelProps={{
                sx: {
                  "& .MuiFormLabel-asterisk": {
                    color: "red",
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Mobile No"
              type="tel"
              value={formData.email}
              onChange={(e) => {
                // Allow only numeric digits and limit to 10 digits
                const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                setFormData({ ...formData, email: value });
                // Clear error when user starts typing
                if (errors.email) {
                  setErrors((prev) => ({ ...prev, email: "" }));
                }
              }}
              margin="normal"
              required
              error={!!errors.email}
              helperText={errors.email}
              inputProps={{
                maxLength: 10,
              }}
              InputLabelProps={{
                sx: {
                  "& .MuiFormLabel-asterisk": {
                    color: "red",
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) => {
                setFormData({ ...formData, password: e.target.value });
                // Clear error when user starts typing
                if (errors.password) {
                  setErrors((prev) => ({ ...prev, password: "" }));
                }
              }}
              margin="normal"
              required
              error={!!errors.password}
              helperText={errors.password}
              InputLabelProps={{
                sx: {
                  "& .MuiFormLabel-asterisk": {
                    color: "red",
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                  onClick={() => refreshCaptcha()}
                  disabled={captchaQuery.isFetching}
                  aria-label="Refresh captcha"
                >
                  <RefreshIcon />
                </IconButton>
                <Box
                  component="img"
                  src={captchaUrl ?? ""}
                  alt="Captcha"
                  sx={{
                    height: 48,
                    width: 150,
                    border: "none",
                    // borderRadius: "8px",
                    bgcolor: "white",
                  }}
                />
                <TextField
                fullWidth
                label="Enter Captcha"
                value={captchaValue}
                onChange={(e) => {
                  setCaptchaValue(e.target.value.slice(0, 5));
                  if (errors.captcha) setErrors((prev) => ({ ...prev, captcha: "" }));
                }}
                margin="normal"
                required
                error={!!errors.captcha}
                helperText={errors.captcha}
                inputProps={{
                  maxLength: 5,
                }}
              />
              </Box>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isLoading}
              sx={{
                mt: 4,
                mb: 3,
                py: 2,
                fontWeight: "bold",
                fontSize: 16,
                backgroundColor: "#3092e3",
              }}
            >
              {isLoading ? "Registering..." : "Register"}
            </Button>
            <Box sx={{ textAlign: "center", mt: 3 }}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Already have an account?{" "}
                <Button
                  variant="text"
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontWeight: "bold",
                    color: "#3092e3",
                  }}
                  onClick={() => navigate("/login?type=student")}
                >
                  Login
                </Button>
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Register;
